import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FiDialogsContentBase } from '@fi-shared/fi-dialogs/fi-dialogs-content-base';
import { FiAIAssistanceService } from '@fi-shared/fi-ai-assistance/fi-ai-assistance.service';
import { BehaviorSubject } from 'rxjs';
import { FiAIAssistanceType } from '@fi-shared/fi-ai-assistance/fi-a-i-assistance-type';
import {
    FiAiAssistanceResponseStatus,
    IAIAssistancePrompt,
    IAIChecklist2Model,
    IAIRequestHistoryModel,
} from '@fi-shared/fi-ai-assistance/fi-ai-assistance';
import { FiDialogCloseReason } from '@fi-shared/fi-dialogs/fi-dialogs-close-reason';
import { FiValuesComparison } from '@fi-shared/utils/fi-values-comparison';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiChecklistModuleDiscardChangesDialogData } from '@fi-app/shared/checklist/fi-checklist-helper.service';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';

export interface IChecklistAiAssistanceDialogData {
    selectedLanguageId: FiApplicationLanguageType;
}

export interface IChecklistAiAssistanceDialogResult {
    aiResponse: IAIChecklist2Model;
}

@Component({
    selector: 'fi-checklist-ai-assistance-dialog',
    templateUrl: './checklist-ai-assistance-dialog.component.html',
    styleUrl: './checklist-ai-assistance-dialog.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist-ai-assistance-dialog' },
    standalone: false,
})
export class FiChecklistAiAssistanceDialogComponent
    extends FiDialogsContentBase<IChecklistAiAssistanceDialogData, IChecklistAiAssistanceDialogResult>
    implements OnInit
{
    protected loaded$ = new BehaviorSubject<boolean>(false);
    protected loadingAIResponse$ = new BehaviorSubject<boolean>(false);
    protected aiResponseStatus$ = new BehaviorSubject<FiAiAssistanceResponseStatus>(undefined);

    protected aiPromptSamples$ = new BehaviorSubject<IAIAssistancePrompt[]>([]);
    protected recentAIRequests$ = new BehaviorSubject<IAIRequestHistoryModel[]>([]);
    protected aiRequestMessage: string = '';
    protected aiResponse: IAIChecklist2Model;
    private fiValuesComparison: FiValuesComparison<{ aiRequestMessage: string }>;

    public ngOnInit(): void {
        this.setServices();
        this.loadAiRequests().finally(() => this.loaded$.next(true));
        this.fiValuesComparison = new FiValuesComparison(() => ({
            aiRequestMessage: this.aiRequestMessage,
        }));
    }

    private loadAiRequests(): Promise<[void, void]> {
        /*const promptSamplesPromise = new Promise<void>((resolve, reject) => {
            this.fiAIAssistanceService.getAIPromptSamples(FiAIAssistanceType.ChecklistV2).then(result => {
                this.aiPromptSamples$.next(result);
                resolve();
            }, reject)
        });*/

        const promptSamplesPromise: Promise<void> = Promise.resolve();

        const recentAIRequestsPromise = new Promise<void>((resolve, reject) => {
            this.fiAIAssistanceService.getRecentAIRequests(FiAIAssistanceType.ChecklistV2).then((result) => {
                this.recentAIRequests$.next(result);
                resolve();
            }, reject);
        });

        return Promise.all([promptSamplesPromise, recentAIRequestsPromise]);
    }

    protected sendRequestToAI(message: string): void {
        this.resetAiResponseStatus();
        this.loadingAIResponse$.next(true);
        this.aiRequestMessage = message;
        this.fiAIAssistanceService
            .getChecklistFromAI(message, this.data.selectedLanguageId)
            .then((aiResponse: IAIChecklist2Model) => {
                this.aiResponse = aiResponse;
                this.aiResponseStatus$.next(FiAiAssistanceResponseStatus.Success);
                // this.aiResponseStatus$.next(aiResponse.Status);
                this.cdr.markForCheck();
                this.loadAiRequests();
            })
            .finally(() => this.loadingAIResponse$.next(false));
    }

    protected isAiRequestSucceed(): boolean {
        return this.aiResponseStatus$.value === FiAiAssistanceResponseStatus.Success;
    }

    protected closeAIResponseViewer(): void {
        this.getNoButtonClickPromise().then(({ canClose }) => {
            if (canClose) {
                this.resetAiResponseStatus();
                this.cdr.markForCheck();
            }
        });
    }

    protected resetAiResponseStatus(): void {
        this.aiResponseStatus$.next(undefined);
        this.aiResponse = undefined;
    }

    protected addCheckpoints(): void {
        this.dialogRef.close({
            reason: FiDialogCloseReason.YesButton,
            value: {
                aiResponse: this.aiResponse,
            },
        });
    }

    public getNoButtonClickPromise(): Promise<{ canClose: boolean }> {
        return this.fiDialogsService.getCanCloseConfirmation(!this.fiValuesComparison.isChanged());
    }

    protected cancel(): void {
        this.getNoButtonClickPromise().then(({ canClose }) => {
            if (canClose) {
                this.dialogRef.close({ reason: FiDialogCloseReason.NoButton });
            }
        });
    }

    private fiAIAssistanceService: FiAIAssistanceService;
    private cdr: ChangeDetectorRef;
    private fiDialogsService: FiDialogsService;
    private setServices() {
        this.fiAIAssistanceService = this.injector.get(FiAIAssistanceService);
        this.cdr = this.injector.get(ChangeDetectorRef);
        this.fiDialogsService = this.injector.get(FiDialogsService);
    }
}
